import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HeroSection = () => {
    const heroRef = useRef(null);
    const textRef = useRef(null);
    const imageRef = useRef(null);
    const backgroundRef = useRef(null);
    const [activeBackgroundIndex, setActiveBackgroundIndex] = useState(0);

    const heroBackgrounds = [
        'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=3840&q=90',
        'https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=3840&q=90',
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=3840&q=90',
        'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?auto=format&fit=crop&w=3840&q=90',
        'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=3840&q=90',
        'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=3840&q=90',
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveBackgroundIndex((prevIndex) => (prevIndex + 1) % heroBackgrounds.length);
        }, 4000);

        return () => clearInterval(interval);
    }, [heroBackgrounds.length]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Parallax effect for background
            gsap.to(backgroundRef.current, {
                y: 200,
                ease: 'none',
                scrollTrigger: {
                    trigger: heroRef.current,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 1,
                },
            });

            // Text animation on scroll
            gsap.fromTo(
                textRef.current,
                {
                    opacity: 0,
                    y: 100,
                    scale: 0.9,
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 1.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: heroRef.current,
                        start: 'top 80%',
                        end: 'top 20%',
                        scrub: 1,
                    },
                }
            );

            // Image animation on scroll
            gsap.fromTo(
                imageRef.current,
                {
                    opacity: 0,
                    x: 100,
                    scale: 0.9,
                },
                {
                    opacity: 1,
                    x: 0,
                    scale: 1,
                    duration: 1.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: heroRef.current,
                        start: 'top 80%',
                        end: 'top 20%',
                        scrub: 1,
                    },
                }
            );
        }, heroRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={heroRef}
            id="home"
            className="hero-section relative overflow-hidden min-h-screen flex items-center justify-center"
        >
            {/* Parallax Background */}
            <div ref={backgroundRef} className="absolute inset-0 z-0">
                {heroBackgrounds.map((bgImage, index) => (
                    <div
                        key={bgImage}
                        className={`absolute inset-0 full-bg-section transition-opacity duration-1000 ${index === activeBackgroundIndex ? 'opacity-100' : 'opacity-0'
                            }`}
                        style={{ backgroundImage: `url('${bgImage}')` }}
                    />
                ))}
            </div>

            {/* Dark gradient overlay for text readability */}
            <div className="hero-overlay absolute inset-0 z-10"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 md:pt-36 pb-16 w-full relative z-20">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Hero Text */}
                    <div ref={textRef} className="text-white">
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                            Your Global Education Journey Starts Here
                        </h1>
                        <p className="text-xl md:text-2xl mb-4 text-primary-red font-semibold">
                            No.1 Consultant for Australia & New Zealand | 95% Visa Success Rate
                        </p>
                        <p className="text-lg md:text-xl mb-8 text-gray-100 leading-relaxed">
                            Swan Global is a trusted study abroad consultancy in Hyderabad specializing in Australia, New Zealand, USA, UK & Canada.
                        </p>
                        <div className="hero-buttons flex flex-wrap gap-4">
                            <a
                                href="mailto:info@swanglobaledu.com?subject=Book%20Free%20Consultation%20Inquiry"
                                className="btn-primary inline-flex items-center gap-2 animate-pulse-glow"
                            >
                                <i className="fas fa-calendar-check"></i>
                                Book Free Consultation
                            </a>
                            <a
                                href="https://wa.me/919000329999"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-secondary inline-flex items-center gap-2"
                            >
                                <i className="fab fa-whatsapp"></i>
                                WhatsApp Now (+91-9000329999)
                            </a>
                        </div>
                    </div>

                    {/* Hero Image - Student studying abroad */}
                    <div ref={imageRef} className="hidden lg:block relative">
                        <img
                            src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=90"
                            alt="Student studying abroad"
                            className="rounded-2xl shadow-2xl w-full"
                            loading="lazy"
                        />
                        <div className="absolute -bottom-6 -right-6 bg-primary-red text-white p-6 rounded-xl text-center shadow-xl animate-float">
                            <span className="block text-3xl font-extrabold">95%</span>
                            <span className="text-sm">Visa Success Rate</span>
                        </div>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default HeroSection;
